import { useEffect, useState } from "react";
import { Bell, BookOpen, LogOut, Mail, X } from "lucide-react";

import { ROLES } from "./consts";
import { Context } from "./context";
import LoginScreen from "./loginScreen";
import StudentWorkspace from "./student/student-workspace";
import TeacherWorkspace from "./teacher/teacher-workspace";
import { callApi, fmtDate, formatDT } from "./utils";

const getCurrentDate = () => {
  const getStrFromNum = (num) => String(num).padStart(2, "0");

  const d = new Date();
  return `${d.getFullYear()}-${getStrFromNum(d.getMonth() + 1)}-${getStrFromNum(d.getDate())}`;
};

export default function App() {
  const [auth, setAuth] = useState(null);
  const [today, setToday] = useState(getCurrentDate());
  const [todayFomatted, setTodayFormatted] = useState(
    new Date().toLocaleDateString(),
  );
  const [reviewStatuses, setReviewStatuses] = useState([]);
  const [maxCommentLength, setMaxCommentLength] = useState(5000);
  const [messagesObj, setMessagesObj] = useState(null);
  const [messagesOpen, setMessagesOpen] = useState(false);

  useEffect(() => {
    callApi({ method: "GET", path: "api/system/date" }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const date = response.response?.date;
        if (date) {
          setToday(date);
          setTodayFormatted(fmtDate(date));
        } else {
          console.error(response.response);
        }
      }
    });

    callApi({
      method: "GET",
      path: "api/review-statuses",
    }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const respArr = response.response;
        if (respArr) {
          setReviewStatuses(respArr);
        } else {
          console.error(respArr);
        }
      }
    });

    callApi({
      method: "GET",
      path: "api/platform-settings",
    }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const value = response.response?.maxCommentLength;
        if (value) {
          setMaxCommentLength(value);
        } else {
          console.error(response.response);
        }
      }
    });
  }, []);

  const getMessages = () => {
    setMessagesObj(null);

    callApi({
      method: "GET",
      path: "api/student/messages",
      needAuth: true,
    }).then((response) => {
      const err = response.error;
      if (err) {
        console.error(err);
      } else {
        const respObj = response.response;
        if (respObj) {
          setMessagesObj(respObj);
        } else {
          console.error(respObj);
        }
      }
    });
  };

  useEffect(() => {
    if (messagesObj?.unreadCount && !messagesOpen) {
      callApi({
        method: "POST",
        path: "api/student/messages/mark-read",
        needAuth: true,
      }).then(getMessages);
    }
  }, [messagesOpen]);

  const handleLogin = (userData) => {
    setAuth(userData);

    if (userData.role === ROLES.student) {
      getMessages();
    }
  };

  const handleLogout = () => {
    setAuth(null);
    sessionStorage.clear();
  };

  const renderContent = () => {
    if (!auth) {
      return <LoginScreen onLogin={handleLogin} />;
    }

    const isTeacher = auth.role === ROLES.teacher;

    return (
      <>
        <header className="topbar">
          <div className="topbar-left">
            <div className="brand">
              <BookOpen size={18} /> Практика
            </div>
            <span className="today">Сегодня: {todayFomatted}</span>
          </div>
          <div className="topbar-right">
            {!isTeacher && (
              <button
                className="icon-btn"
                onClick={() => setMessagesOpen((v) => !v)}
                aria-label="Сообщения"
              >
                <Bell size={18} />
                {!!messagesObj?.unreadCount && (
                  <span className="dot">{messagesObj.unreadCount}</span>
                )}
              </button>
            )}
            <div className="who">
              <span className="who-name">{auth.fullName}</span>
              <span className="who-role">
                {isTeacher ? "Преподаватель" : `Студент · ${auth.groupNumber}`}
              </span>
            </div>
            <button
              className="icon-btn"
              onClick={handleLogout}
              aria-label="Выйти"
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>

        {messagesOpen && (
          <div className="message-panel">
            <div className="message-head">
              <span>
                <Mail size={14} /> Сообщения
              </span>
              <button
                className="icon-btn"
                onClick={() => setMessagesOpen(false)}
              >
                <X size={14} />
              </button>
            </div>
            {messagesObj?.messages?.length ? (
              messagesObj.messages.map((m) => (
                <details key={m.id} className="message-item" open={!m.read}>
                  <summary>
                    <span className="message-subject">{`Практика № ${m.practicalNumber}`}</span>
                    <span className="message-date">
                      {formatDT(m.createdAt)}
                    </span>
                  </summary>
                  <pre>{m.text}</pre>
                </details>
              ))
            ) : (
              <p className="muted small" style={{ padding: 12 }}>
                Пока нет сообщений.
              </p>
            )}
          </div>
        )}

        <main>{isTeacher ? <TeacherWorkspace /> : <StudentWorkspace />}</main>
      </>
    );
  };

  return (
    <Context value={{ maxCommentLength, reviewStatuses, today }}>
      <div className="app-root">{renderContent()}</div>
    </Context>
  );
}
